import React, { FC } from 'react';

import { useHistory, useLocation, Redirect } from 'react-router-dom';

import { Loader } from '../../components/Loader';
import LoginWithPopupTriggerView from '../../components/LoginWithPopupTriggerView/LoginWithPopupTriggerView';
import { ONLINE_CONTENT_CONNECTOR_ATTRIBUTES } from '../../context/OnlineContentConnectorContext';
import { LearnerInfo } from '../../types';
import { checkIsIframed } from '../../utils/helpers';
import { useAuth } from '../../utils/hooks';
import useQueryParams from '../../utils/hooks/useQueryParams';

enum SOUCSE_TYPES {
  CSOD = 'csod',
  SABA = 'saba',
}

interface LoginPageProps {
  userInfo: LearnerInfo;
  userLoading: boolean;
}

const checkString = (val: any): string => (typeof val === 'string' ? val : '');
const mapEntries = (cb) => (obj) => Object.fromEntries(Object.entries(obj).map(cb));
const sanitizeKeys = mapEntries(([key, val]) => [key?.replace(/\?/, '') || key, val]);

const getConnectionName = (sanitizedParams) => {
  let connectionName = '';
  const hasConnectionParam =
    sanitizedParams?.connection && typeof sanitizedParams.connection === 'string';
  const source = sanitizedParams?.source?.toLowerCase();
  const shouldGenerateConnectionName = Object.values(SOUCSE_TYPES).includes(source);

  if (hasConnectionParam) {
    if (shouldGenerateConnectionName) {
      const isCustom =
        sanitizedParams.connection === 'cornerstone' &&
        sanitizedParams.source === 'CSOD' &&
        sanitizedParams.subdomain === 'thinkcontent';

      if (isCustom) {
        connectionName = 'cornerstone';
      } else if (source === SOUCSE_TYPES.CSOD) {
        connectionName = `${sanitizedParams.connection}-${source}-${sanitizedParams.subdomain}`;
      } else if (source === SOUCSE_TYPES.SABA) {
        const fqdn = sanitizedParams?.fqdn?.replace(/\./g, '-') || '';
        connectionName = `${sanitizedParams.connection}-${source}-${fqdn}`;
      } else {
        connectionName = sanitizedParams.connection;
      }
    } else {
      connectionName = sanitizedParams.connection;
    }
  }

  return connectionName;
};

const LoginPage: FC<LoginPageProps> = ({ userInfo, userLoading }) => {
  const history = useHistory();
  const location = useLocation();
  const { loginWithRedirect, isInitialized, isLoading, loginWithPopup } = useAuth();
  const params = useQueryParams();
  const sanitizedParams = sanitizeKeys(params);

  if (!(!isInitialized || isLoading || userLoading)) {
    if (!userLoading && userInfo) {
      let stringParams = '';
      if (sanitizedParams?.courseId) {
        const newParams = new URLSearchParams();
        ONLINE_CONTENT_CONNECTOR_ATTRIBUTES.forEach((key) => {
          if (sanitizedParams?.[key]) {
            newParams.append(key, checkString(sanitizedParams?.[key]));
          }
        });

        stringParams = newParams.toString();
      }
      if (sanitizedParams?.redirectUrl) {
        history.replace(`${sanitizedParams.redirectUrl}${stringParams ? '?' + stringParams : ''}`);
      } else {
        history.replace(`/home${stringParams ? '?' + stringParams : ''}`);
      }
    } else {
      const hasPrevUrl =
        history.location?.state?.prevPath && history.location.state.prevPath !== '/';
      const redirectUrl = hasPrevUrl
        ? `${history.location.state.prevPath}${history.location.state.prevSearch || ''}`
        : '/home';
      const hasParamRedirect = Boolean(sanitizedParams?.redirectUrl);

      //check to prevent usual login via /sso
      if (location?.pathname && location.pathname.match(/^\/sso/) && !sanitizedParams?.connection) {
        return <Redirect to="/" />;
      } else {
        const connectionName = getConnectionName(sanitizedParams);

        if (checkIsIframed()) {
          return (
            <LoginWithPopupTriggerView
              isSso={Boolean(connectionName)}
              handleLogin={() =>
                loginWithPopup(connectionName ? { connection: connectionName } : undefined)
              }
            />
          );
        } else {
          loginWithRedirect({
            redirectUri: hasParamRedirect ? (sanitizedParams.redirectUrl as string) : redirectUrl,
            isDirectUrl: !(hasPrevUrl || hasParamRedirect),
            ...(connectionName && {
              connection: connectionName,
            }),
            ...(sanitizedParams?.courseId && {
              appState: {
                onlineContentConnectorData: {
                  userGuid: sanitizedParams?.userGuid,
                  sessionToken: sanitizedParams?.sessionToken,
                  callbackUrl: sanitizedParams?.callbackUrl,
                  fqdn: sanitizedParams?.fqdn,
                  subdomain: sanitizedParams?.subdomain,
                  registrationNumber: sanitizedParams?.registrationNumber,
                  source: sanitizedParams?.source,
                },
              },
            }),
          });
        }
      }
    }
  }

  return <Loader />;
};

export default LoginPage;
